import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, from } from 'rxjs';
import { Referentiel } from 'src/app/data/declaration/models/referentiel.model';
import { ReferentielService } from 'src/app/data/declaration/services/referentiel/referentiel.service';
import { SuiviParcellairesViticoles } from 'src/app/data/suivis/models/suivi-parcellaires-viticoles.model';
import { SuiviParcellairesViticolesService } from 'src/app/data/suivis/services/suivi-parcellaires-viticoles/suivi-parcellaires-viticoles.service';
import * as moment from 'moment';
import { RefCampagnesService } from 'src/app/data/declaration/services/ref-campagnes/ref-campagnes.service';
import { Campagne } from 'src/app/data/declaration/models/campagne.model';
import { Cahier } from 'src/app/data/habilitation/models/cahier.model';
import { CahiersService } from 'src/app/data/habilitation/services/cahiers/cahiers.service';
import { ExportToCsv } from 'export-to-csv';
import { VisuRecapUcComponent } from '../parcellaire-viticole/modals/visu-recap-uc/visu-recap-uc.component';
import { UniteCulturaleService } from 'src/app/data/declaration/services/parcellaire/unite-culturale.service';
import { UniteCulturale } from 'src/app/data/declaration/models/unite-culturale.model';
import { LoaderService } from '../../../core/services/loader/loader.service';
import { map, mergeMap, toArray } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth/auth.service';
import { SessionContext } from '../../../core/services/config/app.settings';
import { OperateursService } from '../../../data/intervenant/services/operateurs/operateurs.service';
import { Operateur } from '../../../data/intervenant/models/operateur.model';
import { OperateurExport } from '../../../data/edition/models/operateur-export.model';

@Component({
    selector: 'app-suivi-parcellaires-viticoles',
    templateUrl: './suivi-parcellaires-viticoles.component.html',
    styleUrls: ['./suivi-parcellaires-viticoles.component.scss'],
    standalone: false
})
export class SuiviParcellairesViticolesComponent implements OnInit {
    public historiques: SuiviParcellairesViticoles[];
    public referentiel: Referentiel;
    public searchForm: UntypedFormGroup;
    public estAffecte = false;
    public campagneEnCours: Campagne;
    public refCahiers: Cahier[];
    public operateurs: OperateurExport[];

    isAdvancedCollapsed = true;

    get debutDate() {
        return this.searchForm.get('debutDate');
    }
    get finDate() {
        return this.searchForm.get('finDate');
    }
    get annee() {
        return this.searchForm.get('annee');
    }
    get non_controlable() {
        return this.searchForm.get('non_controlable');
    }
    get raisonSociale() {
        return this.searchForm.get('raisonSociale');
    }
    get suivi() {
        return this.searchForm.get('suivi');
    }
    get cdcs() {
        return this.searchForm.get('cdcs');
    }

    constructor(
        private readonly loader: LoaderService,
        private readonly suiviParcellaireService: SuiviParcellairesViticolesService,
        private readonly uniteCulturaleService: UniteCulturaleService,
        private readonly fb: UntypedFormBuilder,
        private readonly referentielService: ReferentielService,
        private readonly modalService: NgbModal,
        private readonly refCampagneService: RefCampagnesService,
        private readonly cahiersService: CahiersService,
        private readonly operateursService: OperateursService
    ) {}

    ngOnInit() {
        this.refCampagneService.getCampagneEnCours().subscribe(campagne => {
            this.campagneEnCours = campagne;

            // Instancier le formulaire de recherche
            this.searchForm = this.fb.group({
                raisonSociale: undefined,
                annee: this.campagneEnCours.annee.toString(),
                non_controlable: undefined,
                debutDate: undefined,
                finDate: undefined,
                suivi: false,
                cdcs: undefined
            });

            this.loadData();
        });
    }

    private loadData() {
        forkJoin([
            this.suiviParcellaireService.getSuiviParcellaire('', this.campagneEnCours.annee.toString(), '', null, null, 'false', null),
            this.referentielService.getReferentiel(),
            this.cahiersService.getCahiers(),
            this.operateursService.getOperateursByOrganisme(SessionContext.get('idOrganisme'))
        ])
            .pipe(this.loader.operator())
            .subscribe(([historiques, referentiel, cahiers, operateurs]) => {
                this.historiques = historiques;
                this.referentiel = referentiel;
                this.refCahiers = cahiers;
                this.operateurs = operateurs;
            });
    }

    search() {
        if (this.searchForm.valid) {
            const annee = this.estAffecte ? (this.searchForm.get('annee').value as string) : this.campagneEnCours.annee.toString();
            const raisonSociale = this.searchForm.get('raisonSociale').value as string;
            const non_controlable = this.searchForm.get('non_controlable').value as string;
            const suivi = this.searchForm.get('suivi').value as string;
            const finDate = this.finDate.value;
            const debutDate = this.debutDate.value;
            const expCdc = [];

           (this.cdcs.value ?? this.refCahiers ?? []).forEach(element => {
                expCdc.push(element.id);
            });

            forkJoin(
                this.suiviParcellaireService.getSuiviParcellaire(
                    raisonSociale,
                    annee,
                    non_controlable,
                    this.getMomentDate(debutDate),
                    this.getMomentDate(finDate),
                    suivi,
                    expCdc
                )
            )
                .pipe(this.loader.operator())
                .subscribe(([historiques]) => {
                    this.historiques = historiques;
                });
        }
    }

    /**
     * Transforme une date provenant d'un Datepicker en date momentJS
     * @param date La date provenant d'un Datepicker
     * @see moment.Moment
     */
    private getMomentDate(date: { year: number; month: number; day: number }): moment.Moment {
        return date ? moment([date.year, date.month - 1, date.day, 0, 0, 0]) : null;
    }

    public onEdit(mouvement: SuiviParcellairesViticoles): void {
        this.uniteCulturaleService.getUniteCulturale(mouvement.idUniteCulturale).subscribe(uc => {
            const modal = this.modalService.open(VisuRecapUcComponent, { backdrop: 'static', windowClass: 'app-modal-1000px' });
            modal.componentInstance.uniteCulturale = uc;

            modal.result.then();
        });
    }

    changeType() {
        if (this.suivi.value === 'true') {
            this.estAffecte = true;
        } else if (this.suivi.value === 'false') {
            this.estAffecte = false;
        }
    }

    get suiviLibelle(): string | null {
        const suivi = this.searchForm?.get('suivi')?.value;

        if (suivi === true || suivi === 'true') {
            return 'Parcellaire affecté';
        }

        if (suivi === false || suivi === 'false') {
            return 'Parcellaire courant';
        }

        return null;
    }

    getLibelles(items: any[], property: string): string {
        if (!items?.length) {
            return '';
        }

        return items
            .map(item => item?.[property])
            .filter(Boolean)
            .join(', ');
    }

    hasActiveFiltersParcellaire(): boolean {
        const formValue = this.searchForm?.value;

        return !!(
            formValue?.suivi !== null &&
            formValue?.suivi !== undefined ||
            formValue?.annee ||
            formValue?.raisonSociale ||
            formValue?.debutDate ||
            formValue?.finDate ||
            formValue?.cdcs?.length
        );
    }

    clearField(field: string): void {
        const control = this.searchForm.get(field);

        if (!control) {
            return;
        }

        // Gestion spécifique selon type de champ
        if (Array.isArray(control.value)) {
            control.setValue([]);
        } else {
            control.setValue(null);
        }

        // Cas particulier : type parcellaire
        if (field === 'suivi') {
            control.setValue('false');
            this.changeType();
        }

        // Si on supprime "suivi", on vide aussi l'année
        if (field === 'annee') {
            control.setValue(null);
        }
    }           

    resetSearchForm(): void {
        this.searchForm.reset({
            suivi: 'false',
            annee: null,
            raisonSociale: null,
            cdcs: [],
            debutDate: null,
            finDate: null
        });

        this.changeType();
    }

    exporterParcellaire() {
        const data = [];
        from(this.historiques)
            .pipe(
                mergeMap(exp => this.uniteCulturaleService.getUniteCulturale(exp.idUniteCulturale).pipe(map(uc => ({ exp, uc })))),
                toArray()
            )
            .subscribe(result => {
                result.forEach(({ exp, uc }) => {
                    data.push({
                        raisonSociale: exp.entreprise.raisonSociale,
                        cvi: this.operateurs.filter(op => op.idEntreprise === exp.idEntreprise)[0].cvi,
                        commune: exp.commune,
                        lieuDit: uc.localisation.lieuDit,
                        referenceCadastrale: exp.referenceCadastrale.replace(';', ','),
                        produit: exp.libelleProduit,
                        anneePlantation: exp.anneePlantation,
                        variete: exp.variete,
                        surfacePlantee: exp.surfacePlantee.toFixed(2),
                        surfaceEngagee: exp.surfaceEngagee.toFixed(2),
                        distanceInterRang: uc.distanceInterRang.toFixed(2),
                        distanceIntraRang: uc.distanceIntraRang.toFixed(2),
                        densite: uc.uniteCulturaleVigne.densite.toFixed(0)
                    });
                });

                const options = {
                    filename: 'EXPORT_PARCELLAIRE'.concat('_').concat(moment().format('YYYYMMDD')),
                    fieldSeparator: ';',
                    quoteStrings: '',
                    decimalSeparator: ',',
                    showLabels: true,
                    showTitle: false,
                    title: 'EXPORT_DECLARATION'.concat('_').concat(moment().format('YYYYMMDD')),
                    useTextFile: false,
                    useBom: true,
                    useKeysAsHeaders: false,
                    headers: [
                        'Raison Sociale',
                        "CVI de l'opérateur",
                        'Commune',
                        'Lieu-dit de la parcelle',
                        'Réf. Cadastrale',
                        'Produit',
                        'Année Plantation',
                        'Cépage',
                        'Surface Plantée (ha)',
                        'Surface Engagée (ha)',
                        'Distance inter-rang (m)',
                        'Distance sur rang (m)',
                        'Densité'
                    ]
                };

                const csvExporter = new ExportToCsv(options);

                csvExporter.generateCsv(data);
            });
    }
}
